import type { DirectionFrames, PlayerFrames } from './playerArt';

/**
 * Bounded sprite-clip residency (PRD #624, issue #625): reference-counted,
 * byte-budgeted bookkeeping for the streamed body/equipment atlases the
 * {@link SpriteAtlasStore} keeps resident. The store registers each streamed
 * locomotion bundle and each individually streamed clip here with an estimated
 * decoded byte weight and a destroy callback; consumers (visible sprites, corpses,
 * previews) pin what they are using via retain/release; and whenever the resident
 * total exceeds the active soft budget, unpinned entries are evicted in
 * deterministic least-recently-used order. Pinned residents may exceed the budget
 * temporarily - an in-use resource is never destroyed.
 *
 * Deliberately pure and Pixi-free: "time" is a monotonic use counter (never a wall
 * clock) and byte weights are estimated from frame dimensions, so every behavior
 * here is deterministic and unit-testable without browser heap estimates.
 */

/** Which soft budget applies: the ADR-0308 low-power mode selects the smaller one. */
export type ResidencyMode = 'normal' | 'low-power';

/**
 * Default soft budget for decoded streamed sprite bytes on a desktop client.
 * The full equipment bake decodes to ~460 MB of RGBA; a mixed lobby session was
 * measured around 160 MB decoded (ADR-0308), so 192 MiB keeps a busy bank fully
 * resident while bounding an hours-long mixed-mode session.
 */
export const NORMAL_SOFT_BUDGET_BYTES = 192 * 1024 * 1024;

/**
 * Default soft budget for low-power (coarse-pointer / mobile) clients - roughly the
 * locomotion-only working set ADR-0308 measured (~69 MiB), so a phone holds the
 * visible crowd but sheds uncommon clips and departed looks sooner.
 */
export const LOW_POWER_SOFT_BUDGET_BYTES = 72 * 1024 * 1024;

/** An observable snapshot of the residency state (the testable seam - no heap APIs). */
export interface ResidencyStats {
  mode: ResidencyMode;
  budgetBytes: number;
  residentBytes: number;
  residentCount: number;
  /** Bytes held by entries that are currently pinned (directly or via their parent). */
  pinnedBytes: number;
}

interface ResidentEntry {
  bytes: number;
  refCount: number;
  /** Monotonic use stamp; larger = more recently used. */
  lastUse: number;
  /** Deterministic LRU tie-break: strictly increasing insertion sequence. */
  sequence: number;
  /** Optional owning key (a streamed clip's locomotion bundle). */
  parent?: string;
  /** Child keys registered under this entry, dropped before it on eviction. */
  children: Set<string>;
  destroy: () => void;
}

export interface SpriteResidencyOptions {
  normalBudgetBytes?: number;
  lowPowerBudgetBytes?: number;
  mode?: ResidencyMode;
}

export class SpriteResidency {
  private readonly entries = new Map<string, ResidentEntry>();
  /** Pins taken before (or without) an insert - a look pinned while still streaming. */
  private readonly pendingPins = new Map<string, number>();
  private clock = 0;
  private sequence = 0;
  /** The key being inserted right now, exempt from its own insert's budget pass. */
  private graceKey?: string;
  private normalBudget: number;
  private lowPowerBudget: number;
  private mode: ResidencyMode;

  constructor(opts: SpriteResidencyOptions = {}) {
    this.normalBudget = opts.normalBudgetBytes ?? NORMAL_SOFT_BUDGET_BYTES;
    this.lowPowerBudget = opts.lowPowerBudgetBytes ?? LOW_POWER_SOFT_BUDGET_BYTES;
    this.mode = opts.mode ?? 'normal';
  }

  /** The soft budget the active mode selects. */
  budgetBytes(): number {
    return this.mode === 'low-power' ? this.lowPowerBudget : this.normalBudget;
  }

  /** Switch budget mode (the low-power setting); immediately re-enforces the budget. */
  setMode(mode: ResidencyMode): void {
    if (this.mode === mode) return;
    this.mode = mode;
    this.evictToBudget();
  }

  /** Reconfigure either soft budget independently; re-enforces immediately. */
  setBudgets(budgets: { normalBudgetBytes?: number; lowPowerBudgetBytes?: number }): void {
    if (budgets.normalBudgetBytes !== undefined) this.normalBudget = budgets.normalBudgetBytes;
    if (budgets.lowPowerBudgetBytes !== undefined) this.lowPowerBudget = budgets.lowPowerBudgetBytes;
    this.evictToBudget();
  }

  has(key: string): boolean {
    return this.entries.has(key);
  }

  /** The live reference count of a key (a pinned-but-not-yet-loaded key counts too). */
  refCount(key: string): number {
    return this.entries.get(key)?.refCount ?? this.pendingPins.get(key) ?? 0;
  }

  /**
   * Register a loaded resource. Adopts any pins taken while it was still loading, then
   * enforces the budget (which can only evict *other*, unpinned entries - a fresh
   * insert is stamped most recently used). `parent` links a streamed clip to its
   * locomotion bundle so the clip is protected while the bundle is pinned and is
   * dropped before the bundle when the bundle itself is evicted.
   */
  insert(key: string, bytes: number, destroy: () => void, parent?: string): void {
    const existing = this.entries.get(key);
    const pending = this.pendingPins.get(key) ?? 0;
    this.pendingPins.delete(key);
    const entry: ResidentEntry = {
      bytes: Number.isFinite(bytes) && bytes > 0 ? bytes : 0,
      refCount: (existing?.refCount ?? 0) + pending,
      lastUse: ++this.clock,
      sequence: ++this.sequence,
      parent,
      children: existing?.children ?? new Set(),
      destroy,
    };
    this.entries.set(key, entry);
    if (parent) this.entries.get(parent)?.children.add(key);
    // Enforce the budget, but never evict the entry just inserted (it is about to be
    // used - the whole reason it loaded); if truly unused it falls on the next pass.
    this.graceKey = key;
    try {
      this.evictToBudget();
    } finally {
      this.graceKey = undefined;
    }
  }

  /** Stamp a key as used now (bumps its LRU position). Unknown keys are ignored. */
  touch(key: string): void {
    const entry = this.entries.get(key);
    if (entry) entry.lastUse = ++this.clock;
  }

  /** Take a live reference. Works before the resource loads (a pending pin). */
  retain(key: string): void {
    const entry = this.entries.get(key);
    if (entry) {
      entry.refCount++;
      entry.lastUse = ++this.clock;
    } else {
      this.pendingPins.set(key, (this.pendingPins.get(key) ?? 0) + 1);
    }
  }

  /** Drop a live reference; the final release makes the entry evictable and re-enforces the budget. */
  release(key: string): void {
    const entry = this.entries.get(key);
    if (entry) {
      entry.refCount = Math.max(0, entry.refCount - 1);
      entry.lastUse = ++this.clock;
      if (entry.refCount === 0) this.evictToBudget();
      return;
    }
    const pending = this.pendingPins.get(key);
    if (pending === undefined) return;
    if (pending <= 1) {
      this.pendingPins.delete(key);
      // A pending pin can be protecting children of a never-inserted parent key
      // (a streamed human clip under the essential `body:400`) - re-enforce.
      this.evictToBudget();
    } else {
      this.pendingPins.set(key, pending - 1);
    }
  }

  /**
   * Evict unpinned entries in least-recently-used order (ties broken by insertion
   * order) until the resident total fits the active soft budget. An entry is
   * protected while it, its parent key, or any of its children hold a live
   * reference - pinned residents exceed the budget rather than being destroyed.
   * Returns the evicted keys in eviction order (children before their parent).
   */
  evictToBudget(): string[] {
    const evicted: string[] = [];
    const budget = this.budgetBytes();
    while (this.residentBytes() > budget) {
      const victim = this.pickVictim();
      if (!victim) break; // everything left is pinned: temporary overage
      this.drop(victim, evicted);
    }
    return evicted;
  }

  stats(): ResidencyStats {
    let residentBytes = 0;
    let pinnedBytes = 0;
    for (const [key, entry] of this.entries) {
      residentBytes += entry.bytes;
      if (this.isProtected(key, entry)) pinnedBytes += entry.bytes;
    }
    return {
      mode: this.mode,
      budgetBytes: this.budgetBytes(),
      residentBytes,
      residentCount: this.entries.size,
      pinnedBytes,
    };
  }

  private residentBytes(): number {
    let total = 0;
    for (const entry of this.entries.values()) total += entry.bytes;
    return total;
  }

  private isProtected(key: string, entry: ResidentEntry): boolean {
    if (key === this.graceKey) return true;
    if (entry.refCount > 0) return true;
    if (entry.parent && this.refCount(entry.parent) > 0) return true;
    for (const child of entry.children) {
      if ((this.entries.get(child)?.refCount ?? 0) > 0) return true;
    }
    return false;
  }

  private pickVictim(): string | undefined {
    let victimKey: string | undefined;
    let victim: ResidentEntry | undefined;
    for (const [key, entry] of this.entries) {
      if (this.isProtected(key, entry)) continue;
      if (
        !victim ||
        entry.lastUse < victim.lastUse ||
        (entry.lastUse === victim.lastUse && entry.sequence < victim.sequence)
      ) {
        victim = entry;
        victimKey = key;
      }
    }
    return victimKey;
  }

  /** Remove + destroy one entry (children first, deterministically), recording keys. */
  private drop(key: string, out: string[]): void {
    const entry = this.entries.get(key);
    if (!entry) return;
    for (const child of [...entry.children]) this.drop(child, out);
    this.entries.delete(key);
    if (entry.parent) this.entries.get(entry.parent)?.children.delete(key);
    try {
      entry.destroy();
    } catch {
      // Destruction is best-effort cleanup; a Pixi teardown failure must not stop
      // the budget pass (the entry is already forgotten and can reload fresh).
    }
    out.push(key);
  }
}

/**
 * Reference counts per shared base-atlas URL. Two residency entries can decode the
 * same underlying atlas (the male body and its ghost alias both stream the
 * `human-400-die` atlas, under different keys), so the base texture must only be
 * unloaded after the *final* holder releases it. `unload` fires exactly once per
 * drop-to-zero; a later acquire re-counts from zero, so an evicted atlas reloads
 * normally through Pixi's asset cache.
 */
export class AtlasRefs {
  private readonly counts = new Map<string, number>();

  constructor(private readonly unload: (url: string) => void) {}

  acquire(url: string): void {
    this.counts.set(url, (this.counts.get(url) ?? 0) + 1);
  }

  release(url: string): void {
    const count = this.counts.get(url);
    if (count === undefined) return; // never unload what was never acquired
    if (count <= 1) {
      this.counts.delete(url);
      try {
        this.unload(url);
      } catch {
        // Unloading is an optimization; a cache-eviction failure must not break rendering.
      }
    } else {
      this.counts.set(url, count - 1);
    }
  }

  count(url: string): number {
    return this.counts.get(url) ?? 0;
  }
}

/**
 * Estimate one clip's decoded byte weight as the RGBA bytes of its frames
 * (`w × h × 4` per frame). Deterministic and heap-API-free: computed purely from
 * the frame rectangles the manifest sliced, so tests and the budget agree exactly.
 * Malformed/mocked frames weigh zero rather than throwing.
 */
export function estimateClipBytes(clip: DirectionFrames | undefined): number {
  if (!clip || typeof clip !== 'object') return 0;
  let total = 0;
  for (const frames of Object.values(clip)) {
    if (!Array.isArray(frames)) continue;
    for (const frame of frames) {
      const texture = (frame as { texture?: { width?: unknown; height?: unknown } }).texture;
      const w = typeof texture?.width === 'number' && Number.isFinite(texture.width) ? texture.width : 0;
      const h =
        typeof texture?.height === 'number' && Number.isFinite(texture.height) ? texture.height : 0;
      total += w * h * 4;
    }
  }
  return total;
}

/**
 * Estimate a whole frames record's decoded weight, counting each distinct clip once
 * (creature gait aliases - `run` reusing the walk clip - share one set of frames).
 */
export function estimateFramesBytes(frames: Partial<PlayerFrames>): number {
  const seen = new Set<unknown>();
  let total = 0;
  for (const clip of Object.values(frames)) {
    if (!clip || typeof clip !== 'object' || seen.has(clip)) continue;
    seen.add(clip);
    total += estimateClipBytes(clip as DirectionFrames);
  }
  return total;
}
